import { useState, useEffect } from "react";
import { User, MapPin, Phone, Search, Check, X, Bike } from "lucide-react";
import deliveryAgentsApi from "../../api/delivery-agents";

const OrderAssignDialog = ({ isOpen = false, onClose, order, onAssign }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAgent, setSelectedAgent] = useState(null);

  const [batchNumber, setBatchNumber] = useState(1);
  const [sequenceInBatch, setSequenceInBatch] = useState(1);
  const [batchDate, setBatchDate] = useState("");

  // Fetch delivery agents and initialize batch fields
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setTimeout(() => {
        deliveryAgentsApi.getDeliveryAgents({ isActive: true }).then((res) => {
          setAgents(res.deliveryAgents);
          setLoading(false);
        });
      }, 800);

      if (order) {
        setBatchNumber(order.batchNumber || 1);
        setSequenceInBatch(order.sequenceInBatch || 1);
        
        let d = order.batchDate;
        if (!d && order.deliveryDate) {
          d = new Date(order.deliveryDate).toISOString().split('T')[0];
        }
        if (!d) {
          d = new Date().toISOString().split('T')[0];
        }
        setBatchDate(d);
      }
    }
  }, [isOpen, order]);

  // Handle search
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Filter agents by search term
  const filteredAgents = agents.filter((agent) => {
    const term = searchTerm.toLowerCase();
    return (
      agent.name.toLowerCase().includes(term) ||
      agent.phoneNumber.includes(term) ||
      agent.bikeNumber.toLowerCase().includes(term)
    );
  });

  // Handle agent selection
  const handleSelectAgent = (agent) => {
    setSelectedAgent(agent);
  };

  // Handle assignment confirmation
  const handleConfirmAssignment = () => {
    if (selectedAgent) {
      setSelectedAgent(null);
      onAssign(order, selectedAgent, { batchNumber, sequenceInBatch, batchDate });
      onClose();
    }
  };

  // If dialog is not open, don't render anything
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-30 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">
            Assign Order #{order?._id}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Order Details */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex flex-col md:flex-row justify-between">
            <div>
              <p className="text-sm font-medium text-black-900">
                Customer:{" "}
                <span className="font-normal text-gray-400">
                  {order?.userId?.name}
                </span>
              </p>
              <p className="text-sm font-medium text-black-900 mt-1">
                Address:{" "}
                <span className="font-normal text-gray-400">
                  {order?.deliveryDetails?.address}
                </span>
              </p>
              <p className="text-sm font-medium text-black-900 mt-1">
                Pincode:{" "}
                <span className="font-normal text-gray-400">
                  {order?.deliveryDetails?.postcode}
                </span>
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-black-900 mt-2 md:mt-0">
                Time Slot:{" "}
                <span className="font-normal text-gray-400">
                  {order?.deliveryTime}
                </span>
              </p>
              <p className="text-sm font-medium text-black-900 mt-1">
                Plan:{" "}
                <span className="font-normal text-gray-400">
                  {order?.subscriptionId?.planId?.title}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Batch & Sequencing Fields */}
        <div className="px-6 py-3 border-b border-gray-200 bg-orange-50/50 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
              Batch Number
            </label>
            <select
              value={batchNumber}
              onChange={(e) => setBatchNumber(parseInt(e.target.value))}
              className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-orange-500 focus:border-orange-500 bg-white text-sm outline-none"
            >
              <option value={1}>Batch 1</option>
              <option value={2}>Batch 2</option>
              <option value={3}>Batch 3</option>
              <option value={4}>Batch 4</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
              Sequence
            </label>
            <input
              type="number"
              min={1}
              value={sequenceInBatch}
              onChange={(e) => setSequenceInBatch(parseInt(e.target.value) || 1)}
              className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-1 focus:ring-orange-500 focus:border-orange-500 text-sm outline-none"
            />
          </div>
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search delivery agents..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            />
          </div>
          <div className="flex mt-2 text-xs text-gray-500">
            <span className="mr-4">
              <span className="font-medium">{filteredAgents.length}</span>{" "}
              agents available
            </span>
            {/* {order?.deliveryDetails?.postcode && (
              <span>
                <MapPin className="h-3 w-3 inline mr-1" />
                Searching near {order.deliveryDetails.postcode}
              </span>
            )} */}
          </div>
        </div>

        {/* Agents List */}
        <div className="overflow-y-auto max-h-80">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
              <span className="ml-3 text-gray-500">Loading agents...</span>
            </div>
          ) : filteredAgents.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No delivery agents found matching your search
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {filteredAgents.map((agent) => {
                const isSelected = selectedAgent?._id === agent._id;
                // const isNearbyPostcode =
                //   order?.deliveryDetails?.postcode?.substring(0, 2) ===
                //   agent.postcode.substring(0, 2);

                return (
                  <li
                    key={agent._id}
                    onClick={() => handleSelectAgent(agent)}
                    className={`px-6 py-4 hover:bg-gray-50 cursor-pointer ${
                      isSelected ? "bg-indigo-50" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div
                          className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${
                            isSelected ? "bg-indigo-100" : "bg-gray-100"
                          }`}
                        >
                          <User
                            className={`h-5 w-5 ${
                              isSelected ? "text-indigo-600" : "text-gray-500"
                            }`}
                          />
                        </div>

                        <div className="ml-4">
                          <div className="flex items-center">
                            <p className="font-medium text-gray-900">
                              {agent.name}
                            </p>
                            {/* {isNearbyPostcode && (
                              <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                                Nearby
                              </span>
                            )} */}

                            {agent.availability === "Busy" && (
                              <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full">
                                Busy
                              </span>
                            )}
                          </div>

                          <div className="flex items-center mt-1 text-sm text-gray-500">
                            <Phone className="h-3 w-3 mr-1" />
                            {agent.phoneNumber}
                            <span className="mx-2">•</span>
                            <Bike className="h-3 w-3 mr-1" />
                            {agent.bikeNumber}
                          </div>
                        </div>
                      </div>

                      <div className="text-right">
                        {/* <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-900">
                            {agent.rating}
                            <span className="text-amber-500 ml-1">★</span>
                          </span>
                        </div> */}
                        <p className="text-xs text-gray-500 mt-1">
                          {agent.ordersCount} orders
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmAssignment}
            disabled={!selectedAgent}
            className={`px-4 py-2 rounded-md text-sm font-medium text-white ${
              selectedAgent
                ? "bg-indigo-600 hover:bg-indigo-700"
                : "bg-indigo-300 cursor-not-allowed"
            }`}
          >
            Assign Order
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderAssignDialog;
